import { ApplicationCodes } from '@makemydeal/dr-platform-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';

import { locationStandardsReconciled, locationStandardsStillPending } from '../actions/creators';
import { updateLocationId } from '../actions/paymentActionCreators';
import {
    LOCATION_CHANGE_OFFER_TYPE_META,
    LOCATION_PRICED_OFFER_TYPES,
    SHOPPER_LOCATION_CHANGE_META,
    STANDARDS_PENDING_LOCATION_CHANGE_META
} from '../constants';
import * as types from '../types/actionTypes';
import type { IPaymentConfig, IStateTreeWithFeatureToggles, StandardsPendingLocationChange } from '../types/paymentTypes';
import {
    buildNonConditionalIncentiveKey,
    buildNonConditionalSelectionKeys,
    isProgramIncentivesRestorationEnabled
} from '../utils/incentives';
import {
    getCurrentOfferType,
    getLocationIdQuestion,
    getMappedIdsForAvailableNonConditionalIncentivesByOfferType,
    getPaymentLocationId,
    getSelectedIncentivesByOfferType,
    getSelectedNonConditionalIdsFromPenciledInputs,
    getStandardsPendingLocationChange,
    getZipCode,
    hasRecordedNonConditionalSelection
} from '../selectors';

export const STANDARDS_BEFORE_LOCATION_CHANGE = 'standardsBeforeLocationChange';

type StandardsBeforeLocationChange = { offerType: string; uncheckedIds: number[] };

const getActionZipCode = (action: AnyFSA | undefined): string | undefined =>
    (action?.payload as { zipCode?: string } | undefined)?.zipCode;

const handlesShopperLocationChange = (state: IStateTreeWithFeatureToggles, middlewareConfig: IPaymentConfig): boolean =>
    !middlewareConfig.selectors?.isManagerView &&
    middlewareConfig.sourceApplication !== ApplicationCodes.MV &&
    isProgramIncentivesRestorationEnabled(state);

const isShopperLocationChange = (
    state: IStateTreeWithFeatureToggles,
    action: AnyFSA,
    middlewareConfig: IPaymentConfig
): boolean => {
    if (action.type !== types.FETCH_QUESTIONS_OR_PAYMENT || !handlesShopperLocationChange(state, middlewareConfig)) {
        return false;
    }
    const zipCode = getActionZipCode(action);
    return zipCode !== undefined && zipCode !== getZipCode(state);
};

const getUncheckedStandards = (
    state: IStateTreeWithFeatureToggles,
    offerType: OfferType = getCurrentOfferType(state)
): StandardsBeforeLocationChange | undefined => {
    if (!hasRecordedNonConditionalSelection(state, offerType)) {
        return undefined;
    }
    const selected = new Set<number>(getSelectedNonConditionalIdsFromPenciledInputs(state, offerType));
    const uncheckedIds = getMappedIdsForAvailableNonConditionalIncentivesByOfferType(state, offerType).filter(
        (id: number) => !selected.has(id)
    );
    return { offerType, uncheckedIds };
};

const collectStandardsPendingLocationChange = (state: IStateTreeWithFeatureToggles): StandardsPendingLocationChange => {
    const currentOfferType = getCurrentOfferType(state);
    return LOCATION_PRICED_OFFER_TYPES.filter((offerType) => offerType !== currentOfferType).reduce<StandardsPendingLocationChange>(
        (pending, offerType) => {
            const unchecked = getUncheckedStandards(state, offerType);
            if (!unchecked && getMappedIdsForAvailableNonConditionalIncentivesByOfferType(state, offerType).length === 0) {
                return pending;
            }
            return {
                ...pending,
                [offerType]: unchecked
                    ? { restoresUnchecks: true, uncheckedIds: unchecked.uncheckedIds }
                    : { restoresUnchecks: false }
            };
        },
        {}
    );
};

export const stampShopperLocationChange = (
    state: IStateTreeWithFeatureToggles,
    action: AnyFSA,
    middlewareConfig: IPaymentConfig
): AnyFSA => {
    if (!isShopperLocationChange(state, action, middlewareConfig)) {
        return action;
    }
    const unchecked = getUncheckedStandards(state);
    return {
        ...action,
        meta: {
            ...action.meta,
            [SHOPPER_LOCATION_CHANGE_META]: true,
            [LOCATION_CHANGE_OFFER_TYPE_META]: getCurrentOfferType(state),
            [STANDARDS_PENDING_LOCATION_CHANGE_META]: collectStandardsPendingLocationChange(state),
            ...(unchecked && { [STANDARDS_BEFORE_LOCATION_CHANGE]: unchecked })
        }
    };
};

const isCurrentLocationChangeResponse = (state: IStateTreeWithFeatureToggles, action: AnyFSA): boolean => {
    const originalAction = action.meta?.originalAction as AnyFSA | undefined;
    return (
        action.type === types.PAYMENT_RECEIVED &&
        originalAction?.meta?.[SHOPPER_LOCATION_CHANGE_META] === true &&
        !action.meta?.isStalePaymentResponse &&
        getActionZipCode(originalAction) === getZipCode(state)
    );
};

const reconcileStandardsSelection = (store: any, stamp: StandardsBeforeLocationChange, awaitsLocationReprice: boolean): void => {
    const state: IStateTreeWithFeatureToggles = store.getState();
    const offerType = getCurrentOfferType(state);
    const nonConditionalPrefix = buildNonConditionalIncentiveKey(offerType, '');
    const keptKeys = getSelectedIncentivesByOfferType(state, offerType).filter(
        (key: string | number) => !(typeof key === 'string' && key.startsWith(nonConditionalPrefix))
    );
    const excluded = new Set<number>(stamp.uncheckedIds);
    const availableIds = getMappedIdsForAvailableNonConditionalIncentivesByOfferType(state, offerType);
    const standardIds = availableIds.filter((id: number) => !excluded.has(id));
    const pricedWithUncheckedStandard =
        !(awaitsLocationReprice && getLocationIdQuestion(state)) && standardIds.length < availableIds.length;
    store.dispatch(
        locationStandardsReconciled(
            [...keptKeys, ...buildNonConditionalSelectionKeys(offerType, standardIds, true)],
            pricedWithUncheckedStandard
        )
    );
};

const repriceForResolvedLocation = (store: any): void => {
    const state: IStateTreeWithFeatureToggles = store.getState();
    const locationId = getPaymentLocationId(state);
    if (locationId && getLocationIdQuestion(state)?.options?.length === 1) {
        store.dispatch(updateLocationId(locationId));
    }
};

const reconcilePendingOfferType = (store: any, action: AnyFSA, preActionState: IStateTreeWithFeatureToggles): void => {
    if (action.type !== types.PAYMENT_RECEIVED) {
        return;
    }
    const state: IStateTreeWithFeatureToggles = store.getState();
    const currentOfferType = getCurrentOfferType(state);
    const pendingBefore = getStandardsPendingLocationChange(preActionState);
    const pendingAfter = getStandardsPendingLocationChange(state);
    const stillPending: StandardsPendingLocationChange = {};
    LOCATION_PRICED_OFFER_TYPES.forEach((offerType) => {
        const pending = pendingBefore[offerType];
        if (!pending?.restoresUnchecks || pendingAfter[offerType]) {
            return;
        }
        if (offerType === currentOfferType) {
            reconcileStandardsSelection(store, { offerType, uncheckedIds: pending.uncheckedIds }, false);
        } else {
            stillPending[offerType] = pending;
        }
    });
    if (Object.keys(stillPending).length > 0) {
        store.dispatch(locationStandardsStillPending(stillPending));
    }
};

export const reconcileAfterShopperLocationChange = (
    store: any,
    action: AnyFSA,
    preActionState: IStateTreeWithFeatureToggles = store.getState()
): void => {
    if (!isCurrentLocationChangeResponse(store.getState(), action)) {
        reconcilePendingOfferType(store, action, preActionState);
        return;
    }
    const originalMeta = (action.meta?.originalAction as AnyFSA).meta;
    const stamp = originalMeta?.[STANDARDS_BEFORE_LOCATION_CHANGE] as StandardsBeforeLocationChange | undefined;
    const zipOfferType = originalMeta?.[LOCATION_CHANGE_OFFER_TYPE_META] as OfferType;
    if (zipOfferType !== getCurrentOfferType(store.getState())) {
        store.dispatch(
            locationStandardsStillPending({
                [zipOfferType]: stamp ? { restoresUnchecks: true, uncheckedIds: stamp.uncheckedIds } : { restoresUnchecks: false }
            })
        );
    } else if (stamp) {
        reconcileStandardsSelection(store, stamp, true);
    }
    repriceForResolvedLocation(store);
};
